import { format } from 'date-fns/format';
import { useState } from 'react';
import { toast } from 'sonner';
import queryString from 'query-string';

import { useApiQuery, useFetch } from '@/modules/common';

import { EventParticipantsResponse } from '../types';
import type { EventFiltersType } from "@/modules/eventsV2/hooks";

const currentDate = format(new Date(), 'yyyy-MM-dd');

export function useEventParticipants(eventId: string, eventName: string, filters: EventFiltersType) {
  const fetchClient = useFetch();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const getGenerateReportUrl = async () => {
    setIsGeneratingReport(true);
    try {
      const response = await fetchClient(
        `/events/${eventId}/participants/report-csv`,
        {
          method: 'GET',
          asFormData: true,
          responseType: 'blob',
        }
      );
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${eventName}_report_${currentDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const { data: participants, isFetching } =
    useApiQuery<EventParticipantsResponse>(
      `/events/${eventId}/participants?${queryString.stringify({
        searchText: filters.search,
        participantTypeId: filters.type,
        participantStatus: filters.status,
      }, { arrayFormat: 'bracket' })}&pageSize=2000`,
      {
        queryKey: ['eventParticipants', eventId, filters],
        initialData: { items: [] },
      }
    );

  const uploadEventParticipants = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await fetchClient(`/events/${eventId}/participants`, {
        body: formData,
        method: 'PATCH',
        asFormData: true,
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  return {
    isFetching,
    isGeneratingReport,
    getGenerateReportUrl,
    uploadEventParticipants,
    participants: participants?.items || [],
  } as const;
}
